import { useStateValue } from "../../context/StateProvider";
import { formatCurrency } from "../../utils/helper";
import { Eye, EyeOff } from "lucide-react";
import { useCommissionVisibility } from "../../hooks/useCommissionVisibility";

function ComissionBreakDown({ deal }) {
  const [{ user }, dispatch] = useStateValue();
  const {
    visible: commissionsShown,
    canToggle: canToggleCommissions,
    revealed: commissionsRevealed,
    setRevealed: setCommissionsRevealed,
  } = useCommissionVisibility(user);

  // Deal-terms rows that used to live in Deal Summary's grid. They're
  // commission-context (split, cap, broker fee) so they belong in this
  // card. Each row has its own role gate matching the prior Deal Summary
  // visibility rules.
  const showSplitType =
    (user.role === "Admin" ||
      user.role === "Supervisor" ||
      user.role === "Accountant") &&
    deal.splitdescription;
  const showType =
    user.role !== "Employee" && deal.captypedescription;
  const showBrokerAdminFee =
    user.role === "Admin" && deal.brokeradminfee;
  const hasAnyDealTerms = showSplitType || showType || showBrokerAdminFee;

  return (
    <div className="bg-white rounded-xl shadow p-6">
      <div className="border-b border-black/10 pb-3 flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold">Commission Breakdown</h3>
        {canToggleCommissions && (
          <button
            type="button"
            onClick={() => setCommissionsRevealed(!commissionsRevealed)}
            className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-800 flex-shrink-0"
          >
            {commissionsRevealed ? <EyeOff size={14} /> : <Eye size={14} />}
            {commissionsRevealed ? "Hide" : "Show"} commissions
          </button>
        )}
      </div>

      <div className="space-y-3 mt-3">
        {hasAnyDealTerms && (
          <>
            {showSplitType && (
              <div className="flex justify-between text-sm">
                <span>Split Type</span>
                <strong>{deal.splitdescription}</strong>
              </div>
            )}
            {showType && (
              <div className="flex justify-between text-sm">
                <span>Type</span>
                <strong>{deal.captypedescription}</strong>
              </div>
            )}
            {showBrokerAdminFee && (
              <div className="flex justify-between text-sm">
                <span>Broker Admin Fee</span>
                <strong>{formatCurrency(deal.brokeradminfee)}</strong>
              </div>
            )}
            <div className="border-t border-gray-100 my-2" />
          </>
        )}

        {commissionsShown && deal.totalCommissionamount !== 0 && (
            <div className="flex justify-between text-sm">
              <span>Total Commission</span>
              <strong>{formatCurrency(deal.totalCommissionamount)}</strong>
            </div>
          )}

        {deal.splitdescription !== "No Split" &&
          (deal.splittype === "Referral"
            ? deal.thirdpartyagentcommissionamount
            : deal.agentCommissionamount) > 0 && (
            <>
              <div className="flex justify-between text-sm">
                <span>
                  {deal.splittype === "Referral"
                    ? "Referral Agent Commission"
                    : "Agent Commission"}
                </span>
                <strong>
                  {deal.splittype === "Referral"
                    ? formatCurrency(deal.thirdpartyagentcommissionamount)
                    : formatCurrency(
                        parseFloat(deal.agentCommissionamount) +
                          parseFloat(deal.agentdiscount),
                      )}
                </strong>
              </div>
              {deal.splittype != "Referral" && deal.agentdiscount > 0 && (
                <div className="flex justify-between text-sm">
                  <span>Agent Discount</span>
                  <strong className="text-red-600">
                    {"- " + formatCurrency(deal.agentdiscount)}
                  </strong>
                </div>
              )}
            </>
          )}

        {deal.splittype === "Referral" && deal.agentCommissionamount > 0 && (
          <>
            <div className="flex justify-between text-sm">
              <span>Agent Commission</span>
              <strong>
                {formatCurrency(
                  parseFloat(deal.agentCommissionamount) +
                    parseFloat(deal.agentdiscount),
                )}
              </strong>
            </div>
            {deal.agentdiscount > 0 && (
              <div className="flex justify-between text-sm">
                <span>Agent Discount</span>
                <strong className="text-red-600">
                  {"- " + formatCurrency(deal.agentdiscount)}
                </strong>
              </div>
            )}
          </>
        )}

        {commissionsShown && (
          <>
            {deal.teamleadCommissionamount !== 0 && (
              <>
                <div className="flex justify-between text-sm">
                  <span>Team Lead Commission</span>
                  <strong>
                    {formatCurrency(
                      parseFloat(deal.teamleadCommissionamount) +
                        parseFloat(deal.teamleaddiscount),
                    )}
                  </strong>
                </div>
                {deal.teamleaddiscount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>Team Lead Discount</span>
                    <strong className="text-red-600">
                      {"- " + formatCurrency(deal.teamleaddiscount)}
                    </strong>
                  </div>
                )}
              </>
            )}
            {deal.leadsourceCommissionamount !== 0 && (
              <div className="flex justify-between text-sm">
                <span>Lead Source Commission</span>
                <strong>
                  {formatCurrency(deal.leadsourceCommissionamount)}
                </strong>
              </div>
            )}
            {deal.bankcommission !== 0 && (
              <div className="flex justify-between text-sm">
                <span>CBR Commission</span>
                <strong>{formatCurrency(deal.bankcommission)}</strong>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default ComissionBreakDown;
